import { useState, useRef, useCallback, useEffect } from "react";
import {
  DEFAULT_CHIP,
  HISTORY_LIMIT,
  DEFAULT_MESSAGE,
} from "../constants/game";
import { colorOf, cap, totalOf, fmt } from "../utils/helpers";
import { placeBetApi } from "../api/gameApi";
import { getProfileApi, getResultsApi } from "../api/userApi";
import { playBalanceOf, saveWallet } from "../utils/wallet";
import { buildBets, newRequestId, toWheelNumber } from "../utils/betPayload";
import { parseResultsResponse, toHistoryItem } from "../utils/results";
import SND from "../utils/sound";

const plain = (text) => ({ text, gold: false, ghost: false, pop: false });

export default function useRoulette({ onCelebrate } = {}) {
  const [balance, setBalance] = useState(0);
  const [shown, setShown] = useState(0);
  const [chip, setChip] = useState(DEFAULT_CHIP);
  const [bets, setBets] = useState({});
  const [stack, setStack] = useState([]);
  const [phase, setPhase] = useState("bet"); // bet | spin | result
  const [fast, setFast] = useState(false);
  const [win, setWin] = useState(null);
  const [message, setMessage] = useState(plain(DEFAULT_MESSAGE));
  const [history, setHistory] = useState([]);
  const nextRoundTimer = useRef(null);
  const [wheelVisible, setWheelVisible] = useState(false);
  const [spinJob, setSpinJob] = useState(null);
  const [floatWin, setFloatWin] = useState(null);

  const roundRef = useRef(null);
  const countRef = useRef(0);
  const histId = useRef(0);
  const busyRef = useRef(false); // double-click rokne ke liye
  const reqRef = useRef(null); // { id, sig } retry ke liye same requestId
  const celebrateRef = useRef(onCelebrate);

  useEffect(() => {
    celebrateRef.current = onCelebrate;
  }, [onCelebrate]);
  useEffect(
    () => () => {
      cancelAnimationFrame(countRef.current);
      SND.rollStop();
    },
    [],
  );
  // game khulte hi user ka wallet balance profile API se lao
  useEffect(() => {
    let alive = true;
    getProfileApi()
      .then((res) => {
        saveWallet(res?.data?.data?.wallet);
        const v = playBalanceOf(res?.data?.data?.wallet);
        if (alive && v !== null && !busyRef.current) {
          cancelAnimationFrame(countRef.current);
          setBalance(v);
          setShown(v);
        }
      })
      .catch((err) => console.error("wallet balance failed", err));
    return () => {
      alive = false;
    };
  }, []);
  // pichle round ke results (history strip) game khulte hi lao - latest 10, descending
  useEffect(() => {
    let alive = true;
    getResultsApi({ page: 1, limit: HISTORY_LIMIT })
      .then((res) => {
        const { list } = parseResultsResponse(res);
        console.log(
          "[history] status:",
          res?.status,
          "rows:",
          list.length,
          res?.data,
        );
        if (!alive) return;
        setHistory(list.slice(0, HISTORY_LIMIT).map(toHistoryItem));
      })
      .catch((err) =>
        console.error(
          "[history] results API failed:",
          err?.response?.status,
          err?.message,
          err?.response?.data,
        ),
      );
    return () => {
      alive = false;
    };
  }, []);
  useEffect(() => {
    if (!floatWin) return undefined;
    const t = setTimeout(() => setFloatWin(null), 1900);
    return () => clearTimeout(t);
  }, [floatWin]);

  const totalBet = totalOf(bets);

  const setBal = (v) => {
    cancelAnimationFrame(countRef.current);
    setBalance(v);
    setShown(v);
  };

  const countUp = useCallback((from, to, ms) => {
    cancelAnimationFrame(countRef.current);
    const t0 = performance.now();
    const step = (now) => {
      const q = Math.min((now - t0) / ms, 1);
      setShown(Math.round(from + (to - from) * q));
      if (q < 1) countRef.current = requestAnimationFrame(step);
    };
    countRef.current = requestAnimationFrame(step);
  }, []);

  const cancelNextRound = () => clearTimeout(nextRoundTimer.current);
  useEffect(() => () => clearTimeout(nextRoundTimer.current), []);

  const newRound = () => {
    cancelNextRound();
    setBets({});
    setStack([]);
    setPhase("bet");
    setWin(null);
    setMessage(plain(DEFAULT_MESSAGE));
  };

  const placeBet = (key) => {
    if (phase === "spin") return;
    cancelNextRound();
    const fresh = phase === "result";
    const b = fresh ? {} : bets;
    const st = fresh ? [] : stack;
    if (fresh) {
      setPhase("bet");
      setWin(null);
    }
    if (balance < chip) {
      setBets(b);
      setStack(st);
      setMessage(plain("Not enough balance"));
      return;
    }
    SND.wake();
    SND.chip();
    setBal(balance - chip);
    setBets({ ...b, [key]: (b[key] || 0) + chip });
    setStack([...st, [key, chip]]);
    setMessage(plain(DEFAULT_MESSAGE));
  };

  const undo = () => {
    if (phase !== "bet" || !stack.length) return;
    const [key, amt] = stack[stack.length - 1];
    const next = { ...bets };
    next[key] -= amt;
    if (!next[key]) delete next[key];
    setBets(next);
    setStack(stack.slice(0, -1));
    setBal(balance + amt);
  };

  const clearBets = () => {
    if (phase === "spin") return;
    cancelNextRound();
    if (phase === "result") {
      newRound();
      return;
    }
    setBal(balance + totalBet);
    setBets({});
    setStack([]);
  };

  const toggleFast = () => setFast((f) => !f);

  // wheel ruk gaya, ball number par land hua (result server se aaya hua hai)
  const handleLand = useCallback(() => {
    const r = roundRef.current;
    if (!r) return;
    setBalance(r.final);
    setMessage({
      text: `${cap(colorOf(r.win))} ${r.win}!`,
      gold: true,
      ghost: false,
      pop: false,
    });
    histId.current += 1;
    const entry = { id: histId.current, n: r.win, color: colorOf(r.win) };
    setHistory((h) => [entry, ...h].slice(0, HISTORY_LIMIT));
  }, []);

  // hold time khatam, result dikhao
  const handleDone = useCallback(() => {
    const r = roundRef.current;
    if (!r) return;
    const n = r.net;
    setWheelVisible(false);
    setPhase("result");
    setMessage({
      text: n > 0 ? `Win ${fmt(n)}` : n === 0 ? "Even" : "No Win",
      gold: true,
      ghost: false,
      pop: n > 0,
    });
    if (n > 0) {
      SND.win(n >= 300);
      if (celebrateRef.current) celebrateRef.current(n >= 300);
      countUp(r.startBal, r.final, 1300);
      setFloatWin({ id: Date.now(), text: "+" + fmt(n) });
    } else {
      setShown(r.final);
      if (n < 0) SND.lose();
    }

    // win/loss message dikhne ke baad: table ke chips hatao + agle round ka friendly message
    clearTimeout(nextRoundTimer.current);
    nextRoundTimer.current = setTimeout(
      () => {
        setBets({});
        setStack([]);
        setWin(null);
        setPhase("bet");
        setMessage({
          text: n > 0 ? "Great spin! Next round" : "Good luck! Next round",
          gold: true,
          ghost: false,
          pop: true,
        });
        // 1.5 sec baad ye message hat kar wapis "Place Your Bets" aa jaye
        nextRoundTimer.current = setTimeout(() => {
          setMessage(plain(DEFAULT_MESSAGE));
        }, 1500);
      },
      n > 0 ? 3000 : 2200,
    );
  }, [countUp]);

  const spin = async () => {
    if (phase !== "bet" || !totalBet || busyRef.current) return;
    cancelNextRound();
    busyRef.current = true;
    SND.wake();
    setPhase("spin");
    setMessage(plain("Placing bet..."));

    try {
      const payloadBets = buildBets(bets);

      // same bets ka retry ho to wahi requestId, bets badli hon to naya
      const sig = JSON.stringify(payloadBets);
      if (!reqRef.current || reqRef.current.sig !== sig) {
        reqRef.current = { id: newRequestId(), sig };
      }

      const res = await placeBetApi({
        requestId: reqRef.current.id,
        bets: payloadBets,
      });
      const body = res?.data;
      const data = body?.data ?? body;

      reqRef.current = null; // success, agli spin me naya id

      const winning = toWheelNumber(data.winningNumber);
      roundRef.current = {
        win: winning,
        startBal: balance,
        net: data.netResult,
        final: Number.isFinite(data.balanceAfter)
          ? data.balanceAfter
          : balance + data.totalWin,
      };

      SND.rollStart();
      setWin(winning);
      setMessage({ text: "", gold: false, ghost: true, pop: false });
      setWheelVisible(true);
      setSpinJob({ id: Date.now(), win: winning, fast });
    } catch (err) {
      setPhase("bet"); // bets table par waise hi rehti hain
      setMessage(
        plain(err?.response?.data?.message || "Network error, try again"),
      );
      if (err?.response) reqRef.current = null; // server ne jawab diya, retry id ki zarurat nahi
    } finally {
      busyRef.current = false;
    }
  };

  return {
    shown,
    chip,
    bets,
    stack,
    phase,
    fast,
    win,
    message,
    history,
    wheelVisible,
    spinJob,
    floatWin,
    totalBet,
    setChip,
    placeBet,
    undo,
    clearBets,
    toggleFast,
    spin,
    handleLand,
    handleDone,
  };
}
